/**
 * Public air traffic for a staff map of Teslyn Cloud (the Operations Hub, a project's map; decision 765: staff only,
 * never clients or Preview as client). Each read is also the watch that keeps the organisation's traffic asked for,
 * and the first one asks Teslyn Cloud to poll at once (MB, 27 Oct 2026: the cloud maps took up to 6 minutes to show
 * the aircraft Teslyn Field already showed). The map reads every 8 s while the first answer is on its way, then every
 * minute, as often as the poller can store new positions (`cloudTrafficReadDelayMs`). The first read brings the
 * 2 hours of trails; each later one only the rows stored since (`mergePublicRows`).
 */
import { useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { cloudTrafficReadDelayMs, mergePublicRows, type PublicTrafficHubRow } from '@teslyn/domain';
import { loadHubPublicTraffic, loadProjectPublicTraffic, type HubPublicTraffic } from '../ops/hubApi.ts';

/** Whose traffic: the organisation's (the Hub) or a project's (its map); null reads nothing. */
export type PublicTrafficTarget =
  | { readonly kind: 'hub'; readonly tenantId: string }
  | { readonly kind: 'project'; readonly projectId: string }
  | null;

export function usePublicTraffic(
  sb: SupabaseClient,
  target: PublicTrafficTarget,
  clock: () => number = Date.now,
): HubPublicTraffic | null {
  const [traffic, setTraffic] = useState<HubPublicTraffic | null>(null);
  const [tick, setTick] = useState(0);
  const key = target
    ? target.kind === 'hub'
      ? `hub:${target.tenantId}`
      : `project:${target.projectId}`
    : '';
  // when this map opened, and the rows it has with the id to read after
  const state = useRef<{ key: string; at: number; rows: PublicTrafficHubRow[]; after: number | null }>({
    key: '',
    at: 0,
    rows: [],
    after: null,
  });
  if (state.current.key !== key) state.current = { key, at: clock(), rows: [], after: null };
  useEffect(() => {
    if (!target) {
      setTraffic(null);
      return undefined;
    }
    let live = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const s = state.current;
    const read =
      target.kind === 'hub'
        ? loadHubPublicTraffic(sb, target.tenantId, s.after)
        : loadProjectPublicTraffic(sb, target.projectId, s.after);
    read
      .then((r) => {
        if (!live) return;
        if (!r.status?.enabled) {
          // off (or not there yet): nothing kept, the next read asks for everything again
          s.rows = [];
          s.after = null;
          setTraffic(r);
          return;
        }
        const merged = mergePublicRows(s.rows, r.rows, clock());
        s.rows = merged.rows;
        s.after = merged.after;
        setTraffic({ status: r.status, rows: merged.rows });
      })
      .catch(() => {
        if (!live) return;
        s.rows = [];
        s.after = null;
        setTraffic(null);
      })
      .finally(() => {
        if (!live) return;
        timer = setTimeout(() => setTick((n) => n + 1), cloudTrafficReadDelayMs(clock() - s.at));
      });
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the target is its key; the clock is read at each read
  }, [sb, key, tick]);
  return traffic;
}
